import { FanforgeUserJwtClaims } from '@hotpot-arcade/fanforge-core/types/auth';
import { createStore, StoreApi } from 'zustand/vanilla';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import { AuthApi, AuthState } from '../types/auth-state';
import { PlatformApi } from '@tofu/platform';
import { WithImmer } from '../types/with-immer';
import { AuthControllerFactory } from './controllers/auth-controller-factory';
import { jwtDecode } from 'jwt-decode';
import { FanforgeApiClient } from '@hotpot-arcade/fanforge-api-client';
import { AuthClient } from './../auth/create-auth';

type AuthArgs = {
  platform: PlatformApi;
  onAuthenticated: (jwt: string) => void;
  apiClient: FanforgeApiClient;
  authClient: AuthClient;
};

export function createAuthStore(args: AuthArgs): WithImmer<StoreApi<AuthState & AuthApi>> {
  const { platform, onAuthenticated, apiClient, authClient } = args;

  const initialState: AuthState = {
    state: 'initialized',
    controller: AuthControllerFactory.create({ platform }),
  };

  const store = createStore<AuthState & AuthApi>()(
    devtools(
      immer<AuthState & AuthApi>((set, get) => {
        return {
          ...initialState,
          onAuthenticated,
          apiClient,

          login: async () => {
            try {
              set((previousState) => ({
                ...previousState,
                state: 'fetching-jwt',
              }));

              const apiClient = get().apiClient;
              const authTokens = await get().controller.getHotpotAuthTokens(apiClient, authClient);
              const jwtClaims = jwtDecode<FanforgeUserJwtClaims>(authTokens.jwt);

              onAuthenticated(authTokens.jwt);
              set((previousState) => ({
                ...previousState,
                state: 'authenticated',
                jwt: authTokens.jwt,
                jwtPayload: jwtClaims,
              }));
            } catch (e) {
              console.error('Error logging in.', e);
              set((previousState) => ({
                ...previousState,
                state: 'unauthenticated',
              }));
            }
          },
        };
      }),
      {
        name: 'AuthStore',
      }
    )
  );
  void store.getState().login(); // <-- try to log in
  return store;
}

export type AuthStoreApi = WithImmer<StoreApi<AuthState & AuthApi>>;
export type AuthStoreApiGetState = AuthStoreApi['getState'];
export type AuthStoreApiSetState = AuthStoreApi['setState'];
